import { AttachmentsView } from "../components/report/AttachmentsView";
import { ExpansionView } from "../components/report/ExpansionView";
import { forwardRef, useState } from "react";
import { ChevronDown, Download } from "lucide-react";
import { fmtCurrency, fmtPeriod, fmtPeriodShort, fmtSignedCompact } from "../lib/format";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function downloadCsv(filename, rows) {
  const csv = rows.map((r) => r.map((cell) => {
    const s = cell == null ? "" : String(cell);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function csvNum(v, format) {
  if (v == null) return "";
  if (format === "percent") return (v * 100).toFixed(1);
  return String(v);
}

function slug(s) {
  return String(s || "report").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function CsvButton({ onClick, testid = "drill-csv" }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-brand-tertiary hover:text-brand-primary transition-colors">
      <Download className="h-3.5 w-3.5" /> CSV
    </button>
  );
}

function imgSrc(url) {
  if (!url) return "";
  return url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
}

function kpiValue(k) {
  if (k.value == null) return "\u2014";
  return k.format === "percent" ? `${(k.value * 100).toFixed(1)}%` : fmtCurrency(k.value);
}

function fmtVal(v, format) {
  if (v == null) return "\u2014";
  if (format === "percent") return `${(v * 100).toFixed(1)}%`;
  if (format === "number") return Number(v).toLocaleString();
  return fmtCurrency(v);
}

function KpiDelta({ k }) {
  if (k.value == null) return null;
  const isPct = k.format === "percent";
  const raw = isPct ? (k.delta_abs != null ? k.delta_abs * 100 : null) : (k.delta_pct ?? k.delta_abs);
  if (raw == null) return <span className="text-[11px] text-brand-tertiary">vs prior period</span>;
  const expense = k.key === "total_expenses";
  const good = expense ? raw < 0 : raw >= 0;
  const dollars = k.format === "currency" && k.delta_abs != null ? fmtSignedCompact(k.delta_abs) : null;
  const pctLabel = k.delta_pct != null ? `${k.delta_pct >= 0 ? "+" : ""}${k.delta_pct.toFixed(1)}%` : null;
  const label = isPct ? `${raw >= 0 ? "+" : ""}${raw.toFixed(1)} pts` : [pctLabel, dollars && (pctLabel ? `(${dollars})` : dollars)].filter(Boolean).join(" ");
  return (
    <span className={`inline-flex flex-wrap items-center gap-x-1 text-[11px] font-mono ${good ? "text-brand-pos" : "text-brand-neg"}`}>
      <span className="whitespace-nowrap">{label}</span> <span className="text-brand-tertiary font-sans whitespace-nowrap">vs prior</span>
    </span>
  );
}

function LocBudgetCell({ g, testid }) {
  if (!g || g.abs == null) return <td className="px-5 py-3 text-right font-mono text-brand-tertiary" data-testid={testid}>{"\u2014"}</td>;
  return (
    <td className={`px-5 py-3 text-right font-mono whitespace-nowrap ${g.favorable ? "text-brand-pos" : "text-brand-neg"}`} data-testid={testid}>
      {fmtSignedCompact(g.abs)}{g.pct != null && Math.abs(g.pct) < 200 && <span className="text-[11px] opacity-80"> ({g.pct >= 0 ? "+" : "\u2212"}{Math.abs(g.pct).toFixed(1)}%)</span>}
    </td>
  );
}

function KpiBudget({ k, gap }) {
  const isOcc = k.format === "percent";
  const d = isOcc ? gap?.occupancy : gap?.deltas?.[k.key];
  if (!d || (isOcc ? d.abs_pts == null : d.abs == null)) return null;
  const sign = (v) => (v >= 0 ? "+" : "\u2212");
  const label = isOcc ? `${sign(d.abs_pts)}${Math.abs(d.abs_pts).toFixed(1)} pts`
    : [d.pct != null ? `${sign(d.pct)}${Math.abs(d.pct).toFixed(1)}%` : null, d.pct != null ? `(${fmtSignedCompact(d.abs)})` : fmtSignedCompact(d.abs)].filter(Boolean).join(" ");
  return (
    <span className={`mt-1 inline-flex flex-wrap items-center gap-x-1 text-[11px] font-mono ${d.favorable ? "text-brand-pos" : "text-brand-neg"}`} data-testid={`report-kpi-budget-${k.key}`}>
      <span className="whitespace-nowrap">{label}</span> <span className="text-brand-tertiary font-sans whitespace-nowrap">vs budget</span>
    </span>
  );
}

function EmptyNote({ label }) {
  return <div className="border border-dashed border-brand-border bg-brand-surface px-5 py-6 text-sm text-brand-tertiary text-center">{label}</div>;
}

// Tiny CSS/SVG bar chart with a zero baseline (handles negative net income).
function MiniBars({ points, accent = "#C5A880" }) {
  if (!points || points.length === 0) return null;
  const W = 640, H = 96, pad = 6;
  const vals = points.map((p) => Number(p.value ?? 0));
  const max = Math.max(...vals, 0);
  const min = Math.min(...vals, 0);
  const range = (max - min) || 1;
  const n = points.length;
  const slot = (W - pad * 2) / n;
  const bw = Math.min(slot * 0.55, 44);
  const yFor = (v) => pad + ((max - v) / range) * (H - pad * 2);
  const zeroY = yFor(0);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" aria-hidden="true">
      <line x1={pad} x2={W - pad} y1={zeroY} y2={zeroY} stroke="#E8E3DB" strokeWidth="1" />
      {points.map((p, i) => {
        const v = Number(p.value ?? 0);
        const cx = pad + slot * i + slot / 2;
        const yv = yFor(v);
        const top = Math.min(yv, zeroY);
        const h = Math.max(Math.abs(yv - zeroY), 1.5);
        const neg = v < 0;
        return <rect key={p.period} x={cx - bw / 2} y={top} width={bw} height={h} rx="1.5" fill={neg ? "#A96147" : accent} opacity="0.92" />;
      })}
    </svg>
  );
}

function ChartLabels({ points }) {
  return (
    <div className="flex mt-1.5">
      {points.map((p) => (
        <span key={p.period} className="flex-1 text-center text-[9px] uppercase tracking-wide text-brand-tertiary">{fmtPeriodShort(p.period)}</span>
      ))}
    </div>
  );
}

// A single-metric drill panel: mini chart + month-by-month table.
function DrillPanel({ label, points, format, accent, csvTestid }) {
  if (!points || points.length === 0) return null;
  return (
    <div className="report-drill bg-brand-hover border border-brand-border border-t-0 px-6 py-6" data-testid="report-drill-panel">
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-[10px] uppercase tracking-[0.2em] text-brand-tertiary">{label} · monthly breakdown</p>
        <CsvButton testid={csvTestid} onClick={() => downloadCsv(`${slug(label)}-monthly.csv`, [["Month", label + (format === "percent" ? " (%)" : "")], ...points.map((p) => [fmtPeriod(p.period), csvNum(p.value, format)])])} />
      </div>
      <MiniBars points={points} accent={accent} />
      <ChartLabels points={points} />
      <table className="w-full text-sm mt-5">
        <tbody>
          {points.map((p) => (
            <tr key={p.period} className="border-b border-brand-border last:border-0">
              <td className="py-2 text-brand-muted">{fmtPeriod(p.period)}</td>
              <td className="py-2 text-right font-mono text-brand-ink">{fmtVal(p.value, format)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Multi-metric drill for a single location row.
function LocationDrillPanel({ label, rows }) {
  if (!rows || rows.length === 0) return null;
  const points = rows.map((r) => ({ period: r.period, value: r.net_income }));
  return (
    <div className="report-drill bg-brand-hover border border-brand-border border-t-0 px-6 py-6" data-testid="report-loc-drill-panel">
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-[10px] uppercase tracking-[0.2em] text-brand-tertiary">{label} · net income by month</p>
        <CsvButton testid="drill-csv-loc" onClick={() => downloadCsv(`${slug(label)}-monthly.csv`, [["Month", "Revenue", "Net Income", "Occupancy (%)"], ...rows.map((r) => [fmtPeriod(r.period), r.total_revenue, r.net_income, r.occupancy_pct != null ? (r.occupancy_pct * 100).toFixed(1) : ""])])} />
      </div>
      <MiniBars points={points} accent="#3B5C45" />
      <ChartLabels points={points} />
      <div className="overflow-x-auto">
      <table className="w-full text-sm mt-5 min-w-[420px]">
        <thead>
          <tr className="text-brand-tertiary border-b border-brand-border">
            <th className="text-left font-normal uppercase tracking-[0.1em] text-[10px] py-2">Month</th>
            <th className="text-right font-normal uppercase tracking-[0.1em] text-[10px] py-2">Revenue</th>
            <th className="text-right font-normal uppercase tracking-[0.1em] text-[10px] py-2">Net Income</th>
            <th className="text-right font-normal uppercase tracking-[0.1em] text-[10px] py-2">Occupancy</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.period} className="border-b border-brand-border last:border-0">
              <td className="py-2 text-brand-muted">{fmtPeriod(r.period)}</td>
              <td className="py-2 text-right font-mono text-brand-muted">{fmtCurrency(r.total_revenue)}</td>
              <td className={`py-2 text-right font-mono ${r.net_income >= 0 ? "text-brand-ink" : "text-brand-neg"}`}>{fmtCurrency(r.net_income)}</td>
              <td className="py-2 text-right font-mono text-brand-muted">{r.occupancy_pct != null ? `${(r.occupancy_pct * 100).toFixed(1)}%` : "\u2014"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function ExpandCue({ active }) {
  return (
    <span data-drill-cue className="absolute top-4 right-3 inline-flex items-center text-brand-tertiary">
      <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${active ? "rotate-180" : ""}`} />
    </span>
  );
}

const ReportView = forwardRef(function ReportView({ report }, ref) {
  const [expandedKpi, setExpandedKpi] = useState(null);
  const [expandedSales, setExpandedSales] = useState(null);
  const [expandedLoc, setExpandedLoc] = useState(null);

  if (!report) return null;
  const { title, intro, location_label, period_label, kpis = [], projects = [], goals = [], sections = [], images = [] } = report;
  const hasAtt = (x) => (x.attachments || []).length > 0;
  const validProjects = projects.filter((p) => (p.title || "").trim() || (p.description || "").trim() || hasAtt(p));
  const validGoals = goals.filter((g) => (g.text || "").trim() || hasAtt(g));
  const validSections = sections.filter((s) => (s.heading || "").trim() || (s.body || "").trim() || hasAtt(s));
  const s = report.sales_summary;
  const closeCompleted = s && s.tours_completed ? s.won_deals / s.tours_completed : null;

  const kpiMonthly = report.kpi_monthly || [];
  const kpiDrill = kpiMonthly.length > 1;
  const kpiPoints = (key) => kpiMonthly.map((m) => ({ period: m.period, value: m[key] }));

  const salesMonthly = report.sales_monthly || [];
  const salesDrill = salesMonthly.length > 1;
  const SALES_DRILL = {
    tours_booked: { label: "Tours Booked", format: "number", points: () => salesMonthly.map((m) => ({ period: m.period, value: m.tours_booked })) },
    tours_completed: { label: "Tours Completed", format: "number", points: () => salesMonthly.map((m) => ({ period: m.period, value: m.tours_completed })) },
    won_deals: { label: "Won Deals", format: "number", points: () => salesMonthly.map((m) => ({ period: m.period, value: m.won_deals })) },
    close_ratio: { label: "Close Ratio", format: "percent", points: () => salesMonthly.map((m) => ({ period: m.period, value: m.tours_completed ? m.won_deals / m.tours_completed : null })) },
  };

  const locMonthly = report.location_breakdown_monthly || {};
  const locDrillAvailable = (loc) => (locMonthly[loc] || []).length > 1;

  return (
    <div className="bg-brand-page text-brand-ink" data-testid="report-view">
      <div ref={ref} className="max-w-[880px] w-full mx-auto px-5 sm:px-14 py-14 bg-brand-page">
        {/* Cover */}
        <header className="border-b border-brand-borderStrong pb-10 mb-12">
          <div className="flex items-center justify-between mb-8">
            <span className="font-serif text-xl tracking-tight text-brand-ink">Bond Collective</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-brand-tertiary">Investor Report</span>
          </div>
          <p className="text-xs uppercase tracking-[0.22em] text-brand-gold mb-4">{location_label} · {period_label}</p>
          <h1 className="font-serif text-5xl sm:text-6xl font-light leading-[1.05] tracking-tight text-brand-ink">{title}</h1>
          {intro && <p className="text-base text-brand-muted leading-relaxed mt-6 max-w-[62ch] whitespace-pre-line">{intro}</p>}
        </header>

        {/* KPIs */}
        {kpis.length > 0 && (
          <section className="mb-14" data-testid="report-kpis">
            <div className="flex items-baseline justify-between mb-4 gap-4 flex-wrap">
              <p className="text-[10px] uppercase tracking-[0.2em] text-brand-tertiary">Financial Summary</p>
              {kpiDrill && <p data-drill-cue className="text-[10px] uppercase tracking-[0.14em] text-brand-gold">Click a metric for the monthly breakdown</p>}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-brand-border border border-brand-border">
              {kpis.map((k, i) => {
                const dark = i === 1;
                const active = expandedKpi === k.key;
                return (
                  <button
                    key={k.key}
                    type="button"
                    disabled={!kpiDrill}
                    onClick={() => kpiDrill && setExpandedKpi(active ? null : k.key)}
                    data-testid={`report-kpi-${k.key}`}
                    className={`text-left p-5 sm:p-6 flex flex-col justify-between min-h-[130px] min-w-0 relative transition-all ${dark ? "bg-brand-ink" : "bg-brand-surface"} ${kpiDrill ? "cursor-pointer" : "cursor-default"} ${active ? (dark ? "ring-1 ring-inset ring-brand-gold" : "ring-1 ring-inset ring-brand-gold") : ""} ${kpiDrill && !dark ? "hover:bg-brand-hover" : ""}`}
                  >
                    <p className={`text-[11px] uppercase tracking-[0.12em] pr-5 ${dark ? "text-brand-page/60" : "text-brand-tertiary"}`}>{k.label}</p>
                    <div className="mt-4">
                      <p className={`font-mono text-sm sm:text-xl tracking-tight whitespace-nowrap ${dark ? "text-brand-page" : "text-brand-ink"}`}>{kpiValue(k)}</p>
                      <div className="mt-1.5 flex flex-col"><KpiDelta k={k} />{report.include_budget && <KpiBudget k={k} gap={report.budget_gap} />}</div>
                    </div>
                    {kpiDrill && <ExpandCue active={active} />}
                  </button>
                );
              })}
            </div>
            {report.include_budget && report.budget_gap?.months?.length > 0 && (
              <p className="text-[11px] text-brand-tertiary mt-2" data-testid="report-budget-note">
                Budget comparison covers {report.budget_gap.months.length === 1 ? fmtPeriod(report.budget_gap.months[0]) : `${fmtPeriod(report.budget_gap.months[0])} \u2013 ${fmtPeriod(report.budget_gap.months[report.budget_gap.months.length - 1])}`}.
              </p>
            )}
            {kpiDrill && expandedKpi && (
              <DrillPanel
                label={(kpis.find((k) => k.key === expandedKpi) || {}).label || "Metric"}
                format={(kpis.find((k) => k.key === expandedKpi) || {}).format}
                points={kpiPoints(expandedKpi)}
                accent="#C5A880"
                csvTestid="drill-csv-kpi"
              />
            )}
          </section>
        )}

        {/* Location breakdown — All Locations only */}
        {report.include_location_breakdown && report.location === "all" && (
          <section className="mb-14" data-testid="report-location-breakdown">
            <div className="flex items-baseline justify-between mb-4 gap-4 flex-wrap">
              <p className="text-[10px] uppercase tracking-[0.2em] text-brand-tertiary">By Location</p>
              {Object.keys(locMonthly).length > 0 && <p data-drill-cue className="text-[10px] uppercase tracking-[0.14em] text-brand-gold">Click a location for its monthly detail</p>}
            </div>
            {(report.location_breakdown || []).length > 0 ? (() => { const locGap = report.include_budget ? (report.budget_gap?.by_location || {}) : {}; const showGap = Object.keys(locGap).length > 0; return (
              <div className="border border-brand-border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-brand-tertiary border-b border-brand-border bg-brand-surface">
                      <th className="text-left font-normal uppercase tracking-[0.1em] text-[10px] px-5 py-3">Location</th>
                      <th className="text-right font-normal uppercase tracking-[0.1em] text-[10px] px-4 py-3">Revenue</th>
                      <th className="text-right font-normal uppercase tracking-[0.1em] text-[10px] px-4 py-3">Net Income</th>
                      <th className="text-right font-normal uppercase tracking-[0.1em] text-[10px] px-5 py-3">Occupancy</th>
                      {showGap && <th className="text-right font-normal uppercase tracking-[0.1em] text-[10px] px-5 py-3" data-testid="report-loc-budget-head" title="Net income vs budget">NI vs Budget</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {report.location_breakdown.map((l) => {
                      const can = locDrillAvailable(l.location);
                      const active = expandedLoc === l.location;
                      return (
                        <tr
                          key={l.location}
                          onClick={() => can && setExpandedLoc(active ? null : l.location)}
                          data-testid={`report-loc-row-${l.location}`}
                          className={`border-b border-brand-border last:border-0 bg-brand-surface transition-colors ${can ? "cursor-pointer hover:bg-brand-hover" : ""} ${active ? "bg-brand-hover" : ""}`}
                        >
                          <td className="px-5 py-3 text-brand-ink">
                            <span className="inline-flex items-center gap-1.5">
                              {l.location}
                              {can && <ChevronDown data-drill-cue className={`h-3.5 w-3.5 text-brand-gold transition-transform duration-200 ${active ? "rotate-180" : ""}`} />}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-brand-muted">{fmtCurrency(l.total_revenue)}</td>
                          <td className={`px-4 py-3 text-right font-mono ${l.net_income >= 0 ? "text-brand-ink" : "text-brand-neg"}`}>{fmtCurrency(l.net_income)}</td>
                          <td className="px-5 py-3 text-right font-mono text-brand-muted">{l.occupancy_pct != null ? `${(l.occupancy_pct * 100).toFixed(1)}%` : "\u2014"}</td>
                          {showGap && <LocBudgetCell g={locGap[l.location]} testid={`report-loc-budget-${l.location}`} />}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {expandedLoc && <LocationDrillPanel label={expandedLoc} rows={locMonthly[expandedLoc]} />}
              </div>
            ); })() : <EmptyNote label="No location data for this period yet." />}
          </section>
        )}

        {/* Sales summary */}
        {report.include_sales && (
          <section className="mb-14" data-testid="report-sales-summary">
            <div className="flex items-baseline justify-between mb-4 gap-4 flex-wrap">
              <p className="text-[10px] uppercase tracking-[0.2em] text-brand-tertiary">Sales &amp; Leasing</p>
              {report.sales_summary && salesDrill && <p data-drill-cue className="text-[10px] uppercase tracking-[0.14em] text-brand-gold">Click a metric for the monthly breakdown</p>}
            </div>
            {report.sales_summary ? (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-brand-border border border-brand-border">
                  {[
                    ["tours_booked", "Tours Booked", report.sales_summary.tours_booked],
                    ["tours_completed", "Tours Completed", report.sales_summary.tours_completed],
                    ["won_deals", "Won Deals", report.sales_summary.won_deals],
                    ["close_ratio", "Close Ratio*", closeCompleted != null ? `${(closeCompleted * 100).toFixed(1)}%` : "\u2014"],
                  ].map(([key, label, val]) => {
                    const active = expandedSales === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        disabled={!salesDrill}
                        onClick={() => salesDrill && setExpandedSales(active ? null : key)}
                        data-testid={`report-sales-${key}`}
                        className={`text-left bg-brand-surface p-5 min-h-[100px] min-w-0 flex flex-col justify-between relative transition-all ${salesDrill ? "cursor-pointer hover:bg-brand-hover" : "cursor-default"} ${active ? "ring-1 ring-inset ring-brand-gold" : ""}`}
                      >
                        <p className="text-[10px] uppercase tracking-[0.12em] text-brand-tertiary pr-5">{label}</p>
                        <p className="font-mono text-xl text-brand-ink mt-3 whitespace-nowrap">{val}</p>
                        {salesDrill && <ExpandCue active={active} />}
                      </button>
                    );
                  })}
                </div>
                {salesDrill && expandedSales && (
                  <DrillPanel
                    label={SALES_DRILL[expandedSales].label}
                    format={SALES_DRILL[expandedSales].format}
                    points={SALES_DRILL[expandedSales].points()}
                    accent="#C5A880"
                    csvTestid="drill-csv-sales"
                  />
                )}
                <p className="text-[11px] text-brand-tertiary mt-2.5">* Close ratio is won deals ÷ completed tours.</p>
              </>
            ) : <EmptyNote label="No sales data for this period yet." />}
          </section>
        )}

        {/* Marketing summary */}
        {report.include_marketing && (
          <section className="mb-14" data-testid="report-marketing-summary">
            <p className="text-[10px] uppercase tracking-[0.2em] text-brand-tertiary mb-4">Marketing (PPC)</p>
            {report.marketing_summary ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-brand-border border border-brand-border">
                {[
                  ["Ad Spend", fmtCurrency(report.marketing_summary.spend)],
                  ["Conversions", report.marketing_summary.conversions],
                  ["Cost / Conversion", fmtCurrency(report.marketing_summary.cpa)],
                  ["Avg. CPC", fmtCurrency(report.marketing_summary.avg_cpc)],
                  ["Clicks", (report.marketing_summary.clicks || 0).toLocaleString()],
                  ["Impressions", (report.marketing_summary.impressions || 0).toLocaleString()],
                  ["CTR", report.marketing_summary.ctr != null ? `${report.marketing_summary.ctr}%` : "\u2014"],
                  ["Conv. Rate", report.marketing_summary.conv_rate != null ? `${report.marketing_summary.conv_rate}%` : "\u2014"],
                ].map(([label, val]) => (
                  <div key={label} className="bg-brand-surface p-5 min-h-[92px] flex flex-col justify-between">
                    <p className="text-[10px] uppercase tracking-[0.12em] text-brand-tertiary">{label}</p>
                    <p className="font-mono text-lg text-brand-ink mt-2 whitespace-nowrap">{val}</p>
                  </div>
                ))}
              </div>
            ) : <EmptyNote label="No marketing data for this period yet." />}
          </section>
        )}

        {report.include_expansion && <ExpansionView items={report.expansion || []} />}

        {/* Projects */}
        {validProjects.length > 0 && (
          <section className="mb-14" data-testid="report-projects">
            <h2 className="font-serif text-3xl font-light text-brand-ink mb-6">Projects &amp; Initiatives</h2>
            <div className="space-y-5">
              {validProjects.map((p, i) => (
                <div key={i} className="flex gap-5">
                  <span className="font-mono text-sm text-brand-gold pt-1 w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <div className="border-b border-brand-border pb-5 flex-1">
                    <p className="font-serif text-xl text-brand-ink">{p.title}</p>
                    {p.description && <p className="text-sm text-brand-muted leading-relaxed mt-1.5 whitespace-pre-line">{p.description}</p>}
                    <AttachmentsView items={p.attachments} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Goals */}
        {validGoals.length > 0 && (
          <section className="mb-14" data-testid="report-goals">
            <h2 className="font-serif text-3xl font-light text-brand-ink mb-6">Goals for Next Quarter</h2>
            <ul className="space-y-3">
              {validGoals.map((g, i) => (
                <li key={i} className="flex gap-3 text-brand-ink">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-brand-primary shrink-0" />
                  <span className="text-base leading-relaxed flex-1">{g.text}<AttachmentsView items={g.attachments} /></span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Additional sections */}
        {validSections.map((s, i) => (
          <section key={i} className="mb-12" data-testid={`report-section-${i}`}>
            {s.heading && <h2 className="font-serif text-3xl font-light text-brand-ink mb-4">{s.heading}</h2>}
            {s.body && <p className="text-base text-brand-muted leading-relaxed whitespace-pre-line max-w-[68ch]">{s.body}</p>}
            <AttachmentsView items={s.attachments} />
          </section>
        ))}

        {/* Images */}
        {images.length > 0 && (
          <section className="mb-6" data-testid="report-images">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {images.map((im) => (
                <figure key={im.id} className="overflow-hidden">
                  <img src={imgSrc(im.url)} alt={im.caption || "Report image"} crossOrigin="anonymous"
                    className="w-full h-auto object-cover border border-brand-border" />
                  {im.caption && <figcaption className="text-xs text-brand-tertiary mt-2">{im.caption}</figcaption>}
                </figure>
              ))}
            </div>
          </section>
        )}

        <footer className="border-t border-brand-border pt-6 mt-12 flex items-center justify-between text-[11px] text-brand-tertiary">
          <span>Bond Collective · Confidential</span>
          <span>{location_label} · {period_label}</span>
        </footer>
      </div>
    </div>
  );
});

export default ReportView;
